// What a render crash looks like to an operator, instead of react-router's
// full-page minified default. Not swallowed: the message is shown, the full error
// goes to the console, and the rest of the shell keeps working.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useRouteError } from "react-router-dom";

import styles from "./RouteErrorBoundary.module.css";
import { HOME_PATH } from "../utils/homePath";

const describe = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const Fallback = ({ error }: { error: unknown }) => (
  <div className={styles.wrap}>
    <h2 className={styles.title}>This page could not be displayed</h2>
    <p className={styles.lede}>
      Something on it failed to render. The rest of the app still works - the
      links above will take you elsewhere.
    </p>
    <pre className={styles.detail}>{describe(error)}</pre>
    <p className={styles.lede}>
      <Link to={HOME_PATH}>Go to the dashboard</Link>
    </p>
  </div>
);

// Router-level: anything thrown rendering a route, including a loader failure.
export const RouteErrorElement = () => {
  const error = useRouteError();
  console.error("route render failed", error);
  return <Fallback error={error} />;
};

// Component-level, for a subtree that should not take its siblings with it.
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: unknown }
> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("render failed", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) return <Fallback error={this.state.error} />;
    return this.props.children;
  }
}
